import type { Bounds } from '@bw/core';
import type { DrawableArea, Orientation, SheetSizeId } from './sheets.js';
import {
  TILE_OVERLAP_MM,
  drawableArea,
  paperToWorldMm,
  worldToPaperMm,
} from './sheets.js';

export type ColourMode = 'colour' | 'mono';
export type DimensionMode = 'overall' | 'gauge' | 'full';
export type JointMode = 'gaps' | 'filled';

export interface SheetSetup {
  readonly sheet: SheetSizeId;
  readonly orientation: Orientation;
  /** 1:ratio. Chosen, never fitted. */
  readonly ratio: number;
  readonly colour: ColourMode;
  readonly showGrid: boolean;
  readonly dimensions: DimensionMode;
  readonly joints: JointMode;
  readonly title: TitleBlock;
}

export interface TitleBlock {
  readonly project: string;
  readonly wall: string;
  readonly drawnBy: string;
  readonly revision: string;
  readonly date: string;
}

export const DEFAULT_SETUP: Omit<SheetSetup, 'title'> = {
  sheet: 'A3',
  orientation: 'landscape',
  ratio: 20,
  colour: 'colour',
  showGrid: false,
  dimensions: 'gauge',
  joints: 'gaps',
};

export interface Tile {
  /** Zero-based sequence, row-major, matching the printed n of m. */
  readonly index: number;
  readonly column: number;
  readonly row: number;
  /** Printed label: row letter, column number — A1, A2, B1. */
  readonly label: string;
  /** The world rectangle this sheet shows. */
  readonly world: Bounds;
}

export interface TilePlan {
  readonly columns: number;
  readonly rows: number;
  readonly tiles: readonly Tile[];
  readonly drawable: DrawableArea;
  readonly ratio: number;
  /** How much paper the whole drawing occupies, before tiling. */
  readonly paperWidthMm: number;
  readonly paperHeightMm: number;
  readonly bounds: Bounds;
}

const ROW_LETTERS = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // I and O omitted, as on drawings

function axisTiles(paperMm: number, drawableMm: number): number {
  if (paperMm <= drawableMm) return 1;
  const step = drawableMm - TILE_OVERLAP_MM;
  if (step <= 0) throw new Error('The sheet is too small to tile at this scale');
  return Math.ceil((paperMm - TILE_OVERLAP_MM) / step);
}

/**
 * Design document E2 — tiling preserves the CHOSEN scale instead of shrinking
 * the drawing to fit. Adjacent tiles overlap by a printed margin so sheets can
 * be trimmed and butted, or folded and lapped.
 */
export function planTiles(bounds: Bounds, setup: SheetSetup): TilePlan {
  const drawable = drawableArea({
    sheet: setup.sheet,
    orientation: setup.orientation,
    showGaugeRod: setup.dimensions !== 'overall',
    // The dimension band along the bottom is always present: overall sizes are
    // the minimum a drawing must carry.
    showDimensions: true,
  });
  const paperWidthMm = worldToPaperMm(bounds.maxX - bounds.minX, setup.ratio);
  const paperHeightMm = worldToPaperMm(bounds.maxY - bounds.minY, setup.ratio);

  const columns = axisTiles(paperWidthMm, drawable.widthMm);
  const rows = axisTiles(paperHeightMm, drawable.heightMm);
  const stepX = drawable.widthMm - (columns > 1 ? TILE_OVERLAP_MM : 0);
  const stepY = drawable.heightMm - (rows > 1 ? TILE_OVERLAP_MM : 0);

  const tileWorldHeight = paperToWorldMm(drawable.heightMm, setup.ratio);
  // A single-row drawing stands on its datum at the foot of the sheet rather
  // than hanging from the top of an over-tall tile.
  const topAnchor = rows === 1 ? bounds.minY + tileWorldHeight : bounds.maxY;

  const tiles: Tile[] = [];
  for (let row = 0; row < rows; row++) {
    for (let column = 0; column < columns; column++) {
      const minX = bounds.minX + paperToWorldMm(column * stepX, setup.ratio);
      // Row 0 is the TOP of the drawing; world Y increases upwards.
      const maxY = topAnchor - paperToWorldMm(row * stepY, setup.ratio);
      tiles.push({
        index: tiles.length,
        column,
        row,
        label: `${ROW_LETTERS[row % ROW_LETTERS.length] ?? 'Z'}${column + 1}`,
        world: {
          minX,
          maxX: minX + paperToWorldMm(drawable.widthMm, setup.ratio),
          maxY,
          minY: maxY - tileWorldHeight,
        },
      });
    }
  }

  return { columns, rows, tiles, drawable, ratio: setup.ratio, paperWidthMm, paperHeightMm, bounds };
}

/** How many sheets a setup produces — shown live as the panel changes. */
export function sheetCount(bounds: Bounds, setup: SheetSetup): number {
  return planTiles(bounds, setup).tiles.length;
}
